import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { AlertCircle, Check, Loader2, Plug, Search, X } from 'lucide-react';
import {
  PROVIDER_CATEGORIES, resolveProvider, type ProviderInfo,
} from '../onboarding/providerCatalog';
import { useComposioConnectFlow } from '../onboarding/appConnect/useComposioConnectFlow';
import type { OnboardingConnectionRow } from '../../services/onboardingApi';
import { AppIcon } from './AppIcon';

/**
 * The stack card's tool half: show what we found, offer to connect it, then let
 * them add what we missed.
 *
 * Order matters and is Josh's: the section's stack QUESTIONS come first (they
 * are the card's field rows, rendered above this), and only then do we show
 * tools. Answering "what do you use" and then seeing those tools appear as
 * something you can connect is the moment the card is for.
 *
 * Everything here already existed somewhere else — the connector catalogue, the
 * Composio popup-and-poll flow, the connection list. None of it was ever shown
 * next to the stack answers.
 */

const CATALOG: ProviderInfo[] = PROVIDER_CATEGORIES.flatMap(c => c.providers);

/** Category label per provider slug, so search results say what a tool is. */
const CATEGORY_OF: Record<string, string> = Object.fromEntries(
  PROVIDER_CATEGORIES.flatMap(c => c.providers.map(p => [p.slug, c.label])),
);

const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '');

export interface StackToolsPanelProps {
  /** Current value of b.tools_used — slugs, or names the user typed. */
  tools: string[];
  connections: OnboardingConnectionRow[];
  clientId?: string;
  /** Persist a new tool list. Rejects to surface an error in place. */
  onToolsChange: (next: string[]) => Promise<unknown>;
  /** Re-read connections after a successful connect. */
  onConnected?: (slug: string) => void;
  /** Registry option labels for b.tools_used, so known values read properly. */
  optionLabels?: Record<string, string>;
}

export const StackToolsPanel: React.FC<StackToolsPanelProps> = ({
  tools, connections, clientId, onToolsChange, onConnected, optionLabels,
}) => {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { runConnectFlow, connectingSlug, connectError, clearConnectError } =
    useComposioConnectFlow(clientId, onConnected);

  const connected = useMemo(() => {
    const live = new Set<string>();
    for (const c of connections || []) {
      const s = String(c.status || '').toLowerCase();
      if (s === 'connected' || s === 'active') live.add(norm(c.provider));
    }
    return live;
  }, [connections]);

  // 'other' is a registry placeholder, not a tool anyone can connect.
  const shown = useMemo(() => tools.filter(t => norm(t) !== 'other'), [tools]);

  const labelFor = (value: string) =>
    optionLabels?.[value] ?? resolveProvider(value).provider.label;

  /** The catalogue entry for a stored value, if we support connecting it. */
  const catalogEntry = (value: string): ProviderInfo | undefined =>
    CATALOG.find(p => norm(p.slug) === norm(value));

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const have = new Set(shown.map(norm));
    return CATALOG
      .filter(p => !have.has(norm(p.slug)))
      .filter(p => p.label.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, shown]);

  const exactInCatalog = useMemo(
    () => CATALOG.some(p => norm(p.label) === norm(query) || norm(p.slug) === norm(query)),
    [query],
  );
  const alreadyHave = useMemo(
    () => shown.some(t => norm(t) === norm(query)),
    [shown, query],
  );

  const addTool = async (value: string) => {
    if (!value.trim()) return;
    setBusy(value);
    setError(null);
    try {
      await onToolsChange([...tools, value]);
      setQuery('');
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(null);
    }
  };

  const removeTool = async (value: string) => {
    setBusy(value);
    setError(null);
    try {
      await onToolsChange(tools.filter(t => t !== value));
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {shown.length > 0 ? 'The tools you told us about' : 'Your tools'}
      </span>

      {shown.length === 0 ? (
        <p className="text-[12.5px] leading-relaxed text-gray-500">
          Nothing yet. Answer the questions above, or search below for the software
          your team works in — connecting one lets us work inside it rather than
          just knowing about it.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {shown.map(tool => {
            const entry = catalogEntry(tool);
            const isConnected = connected.has(norm(tool));
            const isConnecting = entry != null && connectingSlug === entry.slug;
            return (
              <li key={tool}>
                <span
                  className={clsx(
                    'inline-flex items-center gap-2 rounded-xl bg-white py-1.5 pl-2 pr-1.5 ring-1',
                    isConnected ? 'ring-green-200' : 'ring-gray-200',
                  )}
                >
                  <AppIcon value={tool} label={labelFor(tool)} />
                  <span className="text-[12.5px] font-medium text-gray-800">
                    {labelFor(tool)}
                  </span>

                  {isConnected && (
                    <span className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-green-700">
                      <Check size={10} strokeWidth={3} />
                      Connected
                    </span>
                  )}

                  {!isConnected && entry && (
                    <button
                      type="button"
                      disabled={isConnecting}
                      onClick={() => { clearConnectError(); void runConnectFlow(entry); }}
                      className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2 py-0.5 text-[10.5px] font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-60 whitespace-nowrap"
                    >
                      {isConnecting
                        ? <Loader2 size={10} strokeWidth={3} className="animate-spin" />
                        : <Plug size={10} strokeWidth={2.5} />}
                      {isConnecting ? 'Connecting' : 'Connect'}
                    </button>
                  )}

                  {/* A tool we cannot broker is still worth recording — it just
                      says so, rather than showing a Connect that would fail. */}
                  {!isConnected && !entry && (
                    <span className="px-1 text-[10.5px] font-medium text-gray-400">
                      No connection yet
                    </span>
                  )}

                  <button
                    type="button"
                    disabled={busy === tool}
                    onClick={() => { void removeTool(tool); }}
                    className="rounded-md p-0.5 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                    aria-label={`Remove ${labelFor(tool)}`}
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {(error || connectError) && (
        <p role="alert" className="mt-2.5 inline-flex items-start gap-1.5 text-[11.5px] font-medium text-red-600">
          <AlertCircle size={12} strokeWidth={2.5} className="mt-px flex-none" />
          {error || connectError}
        </p>
      )}

      {/* Search everything we can connect, so a tool we failed to detect is one
          keystroke away rather than a support conversation. */}
      <div className="mt-4">
        <label className="relative block">
          <Search
            size={14}
            strokeWidth={2.25}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Add a tool — search Slack, Jira, HubSpot…"
            aria-label="Search tools to add"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-[13px] text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>

        {query.trim() && (
          <div className="mt-2 overflow-hidden rounded-lg ring-1 ring-gray-200">
            {results.map(p => (
              <button
                key={p.slug}
                type="button"
                disabled={busy === p.slug}
                onClick={() => { void addTool(p.slug); }}
                className="flex w-full items-center gap-2.5 border-b border-gray-100 bg-white px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-brand-50/60 disabled:opacity-60"
              >
                <AppIcon value={p.slug} label={p.label} size={16} />
                <span className="text-[13px] font-medium text-gray-800">{p.label}</span>
                <span className="ml-auto text-[11px] text-gray-400">
                  {CATEGORY_OF[p.slug] ?? 'Tool'}
                </span>
              </button>
            ))}

            {/* Not in the catalogue: capture it anyway rather than losing it.
                We just cannot offer a connection for it. */}
            {!exactInCatalog && !alreadyHave && (
              <button
                type="button"
                disabled={busy === query.trim()}
                onClick={() => { void addTool(query.trim()); }}
                className="flex w-full items-center gap-2.5 bg-white px-3 py-2 text-left transition-colors hover:bg-brand-50/60 disabled:opacity-60"
              >
                <AppIcon value={query.trim()} label={query.trim()} size={16} />
                <span className="text-[13px] text-gray-800">
                  Add <span className="font-semibold">{query.trim()}</span>
                </span>
                <span className="ml-auto text-[11px] text-gray-400">Not one we connect</span>
              </button>
            )}

            {results.length === 0 && (exactInCatalog || alreadyHave) && (
              <p className="bg-white px-3 py-2.5 text-[12.5px] text-gray-500">
                {alreadyHave ? 'Already on your stack.' : 'Nothing else matches.'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function messageFor(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (e instanceof Error && e.message) return e.message;
  return "Couldn't update your stack.";
}

export default StackToolsPanel;
